import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Column,
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  HeaderGroup,
  ColumnInstance,
  UseSortByColumnProps,
} from 'react-table';
import { EventData } from '../slices/WellNoteSlice';
import { ReactComponent as PencilIcon } from './../../images/icon-pencil.svg';
import { ReactComponent as TrashIcon } from './../../images/trash.svg';
import './DataTable.scss';
import './TaskDataTable.scss';
import './WellNotesDataTable.scss';
import { ProtectAction } from '../../rbac';
import Loader from '../../features/common/Loader/Loader';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import { DefaultColumnFilter } from './helpers/DefaultColumnFilter';
import { formatDate } from '../../utilities/dateHelpers';

interface WellNotesDataTableProps {
  visibleColumns: string[];
  searchTerm: string;
  wellNotesData: EventData[];
  onEditWellNote: (wellNote: EventData) => void;
  onDeleteWellNote: (wellNote: EventData) => void;
  onUpdateVisibleColumns?: (columns: string[]) => void;
  isConfigCheckBoxEnabled?: boolean;
  onFilteredSortedDataReady?: (data: EventData[]) => void;
  rPermission: { [key: string]: boolean };
  isLoading?: boolean;
  error?: string | null;
}

const WellNotesDataTable: React.FC<WellNotesDataTableProps> = ({
  visibleColumns,
  searchTerm,
  wellNotesData,
  onEditWellNote,
  onDeleteWellNote,
  onFilteredSortedDataReady,
  rPermission,
  isLoading,
  error,
}) => {
  // Filter state management (similar to TaskDataTable)
  const [currentFilters, setCurrentFilters] = useState<unknown[]>([]);
  const data = useMemo(() => wellNotesData || [], [wellNotesData]);

  const currentFiltersRef = useRef(currentFilters);

  useEffect(() => {
    currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
  }, [currentFilters]);

  // Create multi-condition filter with useMemo to prevent recreation
  const multiConditionFilter = useMemo(() => createMultiConditionFilter(setCurrentFilters, currentFiltersRef), []);

  // Clear filter function (similar to TaskDataTable)
  const clearFilterForColumn = useCallback((columnId: string) => {
    setCurrentFilters((prev) =>
      prev.filter((filter: unknown) => (filter as { columnId: string }).columnId !== columnId),
    );
  }, []);

  // Memoize permissions to prevent unnecessary re-renders
  const memoizedPermissions = useMemo(
    () => rPermission,
    [rPermission?.canEdit, rPermission?.canDelete, rPermission?.canAdd, rPermission?.canExport, rPermission?.canView],
  );

  // Base column definitions map for lookup
  const columnMap = useMemo<Record<string, Column<EventData>>>(
    () => ({
      actions: {
        Header: '',
        id: 'actions',
        width: 80,
        Cell: ({ row }: { row: { original: EventData } }) => (
          <div className='tdt-action-cell'>
            {memoizedPermissions?.canEdit && (
              <PencilIcon
                onClick={(e) => {
                  e.stopPropagation();
                  onEditWellNote(row.original);
                }}
                style={{ cursor: 'pointer' }}
              />
            )}
            <ProtectAction canAccess={memoizedPermissions?.canDelete}>
              <TrashIcon onClick={() => onDeleteWellNote(row.original)} />
            </ProtectAction>
          </div>
        ),
      },
      Category: { Header: 'Category', accessor: 'Category', filter: multiConditionFilter },
      Comment: {
        Header: 'Comment',
        accessor: 'Comment',
        Cell: ({ value }: { value: string | undefined }) => <div className='task-multiline-cell'>{value || ''}</div>,
        filter: multiConditionFilter,
      },
      Source: {
        Header: 'Source',
        accessor: 'Source',
        // eslint-disable-next-line no-empty-pattern
        filter: multiConditionFilter,
      },

      Username: { Header: 'Created by', accessor: 'Username', filter: multiConditionFilter },
      Created: {
        Header: 'Created date',
        accessor: 'Created',
        Cell: ({ value }: { value: string | undefined }) => <span>{formatDate(value, false)}</span>,
        filter: multiConditionFilter,
      },
    }),
    [onEditWellNote, onDeleteWellNote, memoizedPermissions, multiConditionFilter],
  );

  // Build columns strictly in the order of visibleColumns
  const columns = useMemo<Column<EventData>[]>(
    () => visibleColumns.map((colId) => columnMap[colId]).filter(Boolean),
    [visibleColumns, columnMap],
  );

  const defaultColumn: any = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [clearFilterForColumn, currentFiltersRef],
  );
  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setGlobalFilter } = tableInstance as any;

  // Apply global search filter
  useEffect(() => {
    setGlobalFilter(searchTerm);
  }, [searchTerm, setGlobalFilter]);

  // Memoize the processed data to prevent unnecessary re-calculations
  const processedData = useMemo(() => {
    return rows.map((row: any) => row.original);
  }, [rows]);

  useEffect(() => {
    if (onFilteredSortedDataReady) {
      onFilteredSortedDataReady(processedData);
    }
  }, [processedData, onFilteredSortedDataReady]);

  if (isLoading) {
    return (
      <div className='is-loading'>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className='error-container'>
        <p>Error loading well notes: {error}</p>
      </div>
    );
  }

  return (
    <section className='table-container-wrapper well-notes-data-table'>
      <div className='table-container mb-[24px]'>
        <div className='task-table-scroll' style={{ maxHeight: 'calc(70vh - 200px)' }}>
          <table {...getTableProps()} className='data-table task-detail-table'>
            <thead>
              {headerGroups.map((headerGroup: HeaderGroup<EventData>) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                  {headerGroup.headers.map((col) => {
                    const column = col as unknown as ColumnInstance<EventData> & UseSortByColumnProps<EventData> as any;
                    return (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className={`h-[44px] pt-[16px] pb-[16px] pl-[12px] pr-[12px] border-t border-[#4a5463] text-left ${column.id}-column`}
                        key={column.id}
                      >
                        <div className='flex gap-[4px] items-center task-detail'>
                          <span className='text-[12px] font-semibold text-[#f7f9f9]'>{column.render('Header')}</span>
                          {column.isSorted && (
                            <span
                              className={`tdt-sort-icon ${column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : 'default'}`}
                              style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                            />
                          )}
                          <span>{column.canFilter ? column.render('Filter') : null}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row: any) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell: any) => (
                      <td
                        {...cell.getCellProps()}
                        className={`${cell.column.id === 'actions' ? 'actions-column' : ''}`}
                        style={{ textAlign: 'left' }}
                        key={cell.column.id}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[250px] flex items-center justify-center'>
              <span className='no-matching-records-message'>No comments available</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WellNotesDataTable;
